import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { STARTING_CAPITAL, fmtUSD, loadMonthlyReturns } from "../util/portfolioSnapshot";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Mirrors Portfolio Builder's buildMonthlyReturnMatrix so the calendars on
// the landing pages and inside PB are computed the same way. Non-compounding
// divides every month by the fixed starting balance; compounding divides by
// the running balance. Months outside the data range stay null so a row's
// leading and trailing cells don't read as flat 0% performance.
export function buildMonthlyMatrix(monthlyMap, startingCapital, compounding = false) {
  const keys = Object.keys(monthlyMap || {}).sort();
  if (!keys.length || !isFinite(startingCapital) || startingCapital <= 0) {
    return { rows: [], endBalance: startingCapital };
  }

  const firstMonth = keys[0];
  const lastMonth = keys[keys.length - 1];
  const firstYear = Number(firstMonth.slice(0, 4));
  const lastYear = Number(lastMonth.slice(0, 4));

  let balance = startingCapital;
  const rows = [];

  for (let year = firstYear; year <= lastYear; year += 1) {
    const months = new Array(12).fill(null);
    const yearStartBalance = balance;
    let yearPnl = 0;

    for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
      const key = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
      if (key < firstMonth || key > lastMonth) continue;

      const pnl = monthlyMap[key] || 0;
      const balanceBefore = balance;
      const returnPct = compounding
        ? (balanceBefore > 0 ? pnl / balanceBefore : null)
        : pnl / startingCapital;
      yearPnl += pnl;
      balance += pnl;
      months[monthIndex] = { pnl, returnPct };
    }

    const yearReturn = compounding
      ? (yearStartBalance > 0 ? (balance / yearStartBalance) - 1 : null)
      : yearPnl / startingCapital;

    rows.push({ year: String(year), months, yearReturn, yearPnl, yearStartBalance, yearEndBalance: balance });
  }

  return { rows, endBalance: balance };
}

function MonthCell({ cell }) {
  if (cell == null) {
    return <td className="px-2 py-2 text-center text-xs text-zinc-700">–</td>;
  }
  const { pnl, returnPct } = cell;
  const isPos = pnl >= 0;
  // Color intensity scales with |returnPct| up to a 5% cap so the
  // brightest green/red shows up at >= +5% / <= -5% in a single month.
  const intensity = returnPct == null ? 0 : Math.min(Math.abs(returnPct) / 0.05, 1);
  const bg = isPos
    ? `rgba(16, 185, 129, ${0.08 + intensity * 0.32})`
    : `rgba(244, 63, 94, ${0.08 + intensity * 0.32})`;
  const text = isPos ? "text-emerald-200" : "text-rose-200";
  return (
    <td
      className={`px-2 py-2 text-center text-xs font-medium tabular-nums ${text}`}
      style={{ background: bg }}
      title={`$${pnl.toLocaleString("en-US", { maximumFractionDigits: 0 })}${
        returnPct == null ? "" : ` (${(returnPct * 100).toFixed(2)}%)`
      }`}
    >
      {returnPct == null ? "" : `${returnPct >= 0 ? "+" : ""}${(returnPct * 100).toFixed(1)}%`}
    </td>
  );
}

function ModeToggle({ compounding, onChange }) {
  const base = "px-3 py-1.5 rounded-md text-xs font-medium transition-colors";
  return (
    <div className="flex rounded-lg border border-white/[0.08] bg-black/40 p-0.5">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`${base} ${
          !compounding ? "bg-accent-green/15 text-accent-green" : "text-zinc-400 hover:text-white"
        }`}
      >
        Non-Compounding
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`${base} ${
          compounding ? "bg-brand text-white" : "text-zinc-400 hover:text-white"
        }`}
      >
        Compounding
      </button>
    </div>
  );
}

// "pb-minimum" labels the starting balance as Portfolio Builder's Minimum
// Capital Required; "fixed" is for surfaces that anchor to their own constant.
export default function MonthlyReturnsHeatmap({
  monthlyMap,
  startingCapital = STARTING_CAPITAL,
  capitalSource = "pb-minimum",
  defaultCompounding = false,
  showToggle = true,
  className = "mt-6",
}) {
  const isPbMinimum = capitalSource === "pb-minimum";
  const [compounding, setCompounding] = useState(defaultCompounding);
  const { rows, endBalance } = buildMonthlyMatrix(monthlyMap, startingCapital, compounding);
  if (!rows.length) return null;
  return (
    <motion.div
      variants={fadeUp}
      className={`rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 md:p-6 max-w-6xl mx-auto ${className}`}
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">
            Monthly returns
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {fmtUSD(startingCapital)} {isPbMinimum ? "minimum capital" : "starting balance"} · default size per strategy
          </p>
        </div>
        {showToggle && <ModeToggle compounding={compounding} onChange={setCompounding} />}
      </div>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full min-w-[640px] border-separate border-spacing-1">
          <thead>
            <tr>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Year
              </th>
              {MONTH_LABELS.map((m) => (
                <th
                  key={m}
                  className="px-1 py-2 text-center text-[10px] uppercase tracking-widest text-zinc-500 font-semibold"
                >
                  {m}
                </th>
              ))}
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Year
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const yearPos = row.yearReturn >= 0;
              return (
                <tr key={row.year}>
                  <td className="px-2 py-2 text-sm font-semibold text-zinc-300 tabular-nums">
                    {row.year}
                  </td>
                  {row.months.map((cell, i) => (
                    <MonthCell key={i} cell={cell} />
                  ))}
                  <td
                    className={`px-2 py-2 text-right text-sm font-bold tabular-nums ${
                      yearPos ? "text-emerald-300" : "text-rose-300"
                    }`}
                    title={
                      compounding
                        ? `${fmtUSD(row.yearStartBalance)} → ${fmtUSD(row.yearEndBalance)}`
                        : `${fmtUSD(row.yearPnl)} on ${fmtUSD(startingCapital)}`
                    }
                  >
                    {row.yearReturn == null
                      ? ""
                      : `${row.yearReturn >= 0 ? "+" : ""}${(row.yearReturn * 100).toFixed(1)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[11px]">
        <p className="text-zinc-600 leading-relaxed max-w-2xl">
          {compounding
            ? "Each cell is the month's PnL divided by the running account balance. The year total compounds those months."
            : "Each cell is the month's PnL divided by the starting balance. The year total is the sum of those months, the same method most published algo calendars use."}{" "}
          {isPbMinimum
            ? "The starting balance is Portfolio Builder's Minimum Capital Required for this portfolio."
            : `The starting balance is ${fmtUSD(startingCapital)}.`}{" "}
          Hover any cell for the dollar PnL.
        </p>
        <p className="text-zinc-400 font-medium whitespace-nowrap">
          {fmtUSD(startingCapital)} <span className="text-zinc-600">→</span>{" "}
          <span className={`font-bold ${endBalance >= startingCapital ? "text-emerald-300" : "text-rose-300"}`}>
            {fmtUSD(endBalance)}
          </span>
        </p>
      </div>
    </motion.div>
  );
}

// Self-loading variant for pages that don't already have the portfolio data
// in hand. Defers the PB CSV fetch until the heatmap is near the viewport so
// it never competes with above-the-fold paint on a landing page.
export function LazyMonthlyReturnsHeatmap({
  className,
  defaultCompounding = false,
  capitalSource = "pb-minimum",
}) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [state, setState] = useState({ status: "idle", data: null });

  useEffect(() => {
    if (!ref.current || shouldLoad) return undefined;
    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: "500px 0px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    let alive = true;
    setState({ status: "loading", data: null });
    loadMonthlyReturns()
      .then((data) => {
        if (alive) setState({ status: "ready", data });
      })
      .catch(() => {
        if (alive) setState({ status: "error", data: null });
      });
    return () => {
      alive = false;
    };
  }, [shouldLoad]);

  return (
    <div ref={ref}>
      {state.status === "ready" && state.data ? (
        <MonthlyReturnsHeatmap
          monthlyMap={state.data.monthlyMap}
          startingCapital={state.data.minimumCapital}
          capitalSource={capitalSource}
          defaultCompounding={defaultCompounding}
          className={className}
        />
      ) : state.status === "error" ? null : (
        <motion.div
          variants={fadeUp}
          className={`rounded-2xl border border-white/[0.06] bg-white/[0.02] max-w-6xl mx-auto h-[320px] animate-pulse ${className || "mt-6"}`}
        />
      )}
    </div>
  );
}
